/**
 * Async Data Diagnosis API Service
 * 
 * Service for handling asynchronous data quality analysis jobs.
 * This service manages job creation, status polling, and result retrieval.
 */

import { httpClient, unwrapApiResult } from './httpClient';
import { apiPath } from '../config/env';
import { authManager } from './authManager';
import { acquireTranslationToken } from './tokenUtils';

export interface AsyncJobRequest {
    file?: File;
    table_name?: string;
    dataset_name: string;
    data_object_id?: string;
    metadata?: Record<string, any>;
    target_language?: string;
    source_language?: string;
}

export interface AsyncJobResponse {
    job_id: string;
    status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED' | 'CANCELLED';
    message: string;
}

export interface JobStatus {
    id: string;
    user_email: string;
    source_type: 'file' | 'databricks';
    source_identifier: string;
    dataset_name: string;
    data_object_id?: string;
    status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED' | 'CANCELLED';
    progress: number;
    current_phase: string;
    error_message?: string;
    created_at: string;
    updated_at: string;
    completed_at?: string;
    historical_report_id?: string;
}

export interface JobResult {
    job_id: string;
    status: string;
    historical_report_id: string;
    target_language: string | null;
    translated_result: JobResult['result'] | null;
    translation_stats: { fields_translated: number } | null;
    result: {
        summary: {
            source_type: string;
            ingestion_success: boolean;
            profiling_success: boolean;
            reasoning_success: boolean;
            deep_analysis_success: boolean;
            deep_analysis_skipped: boolean;
            remediation_success: boolean;
            remediation_skipped: boolean;
            critical_issues_found: number;
            execution_timestamp: string;
        };
        executive_summary: {
            dataset_overview: string;
            data_completeness: string;
            data_reliability: string;
            most_important_issues: string[];
            next_steps: string[];
        };
        health_score: {
            data_quality_assessment_6_dimensions: number;
            completeness: { score: number; reason: string; evidence: string };
            accuracy: { score: number; reason: string; evidence: string };
            consistency: { score: number; reason: string; evidence: string };
            validity: { score: number; reason: string; evidence: string };
            uniqueness: { score: number; reason: string; evidence: string };
            integrity: { score: number; reason: string; evidence: string };
        };
        reasoning_result: {
            problematic_columns: Array<{
                column_name: string;
                issues: Array<{
                    issue_type: string;
                    severity: 'LOW' | 'MEDIUM' | 'HIGH';
                    description: string;
                    recommendation: string;
                }>;
            }>;
        };
        metrics: {
            ingestion_ms: number;
            profiling_ms: number;
            reasoning_ms: number;
            deep_analysis_ms: number;
            remediation_ms: number;
            report_generation_ms: number;
            total_ms: number;
            fallback_used: boolean;
        };
        execution_timestamp: string;
        file_analyzed?: string;
        metadata_used: boolean;
        // DQ Framework Results - rule execution outcomes
        dq_framework_results?: {
            success: boolean;
            column_results: Record<string, {
                rule_results: Array<{
                    rule_id: string;
                    rule_type: string;
                    rule_description: string;
                    pass_rate: number;
                    failing_record_count: number;
                    passing_record_count: number;
                    total_records: number;
                    rule_configuration?: any;
                }>;
                column_pass_rate?: number;
            }>;
            overall_summary?: {
                avg_pass_rate: number;
                total_records: number;
                total_rules: number;
            };
        };
    };
}

class AsyncDataDiagnosisService {
    private pollingJobs = new Map<string, NodeJS.Timeout>();

    /**
     * Start an async data diagnosis job
     */
    async startJob(request: AsyncJobRequest): Promise<AsyncJobResponse> {
        const formData = new FormData();

        if (request.file) {
            formData.append('file', request.file);
        }

        if (request.table_name) {
            formData.append('table_name', request.table_name);
        }

        formData.append('dataset_name', request.dataset_name);
        formData.append('user_email', authManager.getUserEmail());

        if (request.data_object_id) {
            formData.append('data_object_id', request.data_object_id);
        }

        if (request.metadata) {
            formData.append('metadata', JSON.stringify(request.metadata));
        }

        if (request.target_language) {
            formData.append('target_language', request.target_language);
        }

        if (request.source_language) {
            formData.append('source_language', request.source_language);
        }

        // When translation is requested, a Bearer token is required
        const headers: Record<string, string> = {};
        if (request.target_language) {
            const token = await acquireTranslationToken();
            headers['Authorization'] = `Bearer ${token}`;
        }

        const result = await httpClient.post<AsyncJobResponse>(
            apiPath('data-analysis/data-diagnosis/async'),
            formData,
            { headers }
        );

        return unwrapApiResult(result);
    }

    /**
     * Get current status of an async job
     */
    async getJobStatus(jobId: string): Promise<JobStatus> {
        const userEmail = authManager.getUserEmail();
        const result = await httpClient.get<JobStatus>(
            apiPath(`data-analysis/data-diagnosis/async/${jobId}/status?user_email=${encodeURIComponent(userEmail)}`)
        );

        return unwrapApiResult(result);
    }

    /**
     * Get results of a completed async job
     */
    async getJobResult(jobId: string): Promise<JobResult> {
        const userEmail = authManager.getUserEmail();
        const result = await httpClient.get<JobResult>(
            apiPath(`data-analysis/data-diagnosis/async/${jobId}/result?user_email=${encodeURIComponent(userEmail)}`)
        );

        return unwrapApiResult(result);
    }

    /**
     * Poll job status until completion
     */
    async pollJobUntilComplete(
        jobId: string,
        onUpdate: (status: JobStatus) => void,
        intervalMs: number = 25000 // 25 seconds - staggered polling
    ): Promise<JobStatus> {
        return new Promise((resolve, reject) => {
            const poll = async () => {
                try {
                    const status = await this.getJobStatus(jobId);
                    onUpdate(status);

                    if (['SUCCESS', 'FAILED', 'CANCELLED'].includes(status.status)) {
                        this.stopPolling(jobId);
                        resolve(status);
                    } else {
                        const timeoutId = setTimeout(poll, intervalMs);
                        this.pollingJobs.set(jobId, timeoutId);
                    }
                } catch (error) {
                    this.stopPolling(jobId);
                    reject(error);
                }
            };

            poll();
        });
    }

    /**
     * Stop polling for a specific job
     */
    stopPolling(jobId: string): void {
        const timeoutId = this.pollingJobs.get(jobId);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.pollingJobs.delete(jobId);
        }
    }

    /**
     * Stop all polling jobs
     */
    stopAllPolling(): void {
        this.pollingJobs.forEach(timeoutId => clearTimeout(timeoutId));
        this.pollingJobs.clear();
    }

    /**
     * Get all active (PENDING or RUNNING) jobs for the current user
     * Optimized endpoint for dashboard monitoring
     */
    async getRunningJobs(): Promise<JobStatus[]> {
        const userEmail = authManager.getUserEmail();
        const result = await httpClient.get(
            apiPath(`data-analysis/data-diagnosis/async/running-jobs?user_email=${encodeURIComponent(userEmail)}`)
        );

        const data = unwrapApiResult(result) as any;

        // API returns direct array of all jobs, filter to only active ones
        const allJobs = Array.isArray(data) ? data : [];
        const activeJobs = allJobs.filter((job: JobStatus) =>
            job.status === 'RUNNING' || job.status === 'PENDING'
        );

        return activeJobs;
    }

    /**
     * List all jobs for the current user
     */
    async listJobs(options?: {
        status?: string;
        limit?: number;
        offset?: number;
    }): Promise<{
        jobs: JobStatus[];
        total: number;
        limit: number;
        offset: number;
    }> {
        const userEmail = authManager.getUserEmail();
        const params = new URLSearchParams({
            user_email: userEmail,
            ...(options?.status && { status: options.status }),
            ...(options?.limit && { limit: options.limit.toString() }),
            ...(options?.offset && { offset: options.offset.toString() })
        });

        const result = await httpClient.get(
            apiPath(`data-analysis/data-diagnosis/async/jobs?${params}`)
        );

        const data = unwrapApiResult(result) as any;

        // Check if data is directly an array of jobs or has a jobs property
        const jobs = Array.isArray(data) ? data : (data.jobs || []);

        // Return in expected format
        return {
            jobs: jobs,
            total: data.total || jobs.length,
            limit: data.limit || options?.limit || 50,
            offset: data.offset || options?.offset || 0
        };
    }
}

export const asyncDataDiagnosisService = new AsyncDataDiagnosisService();